import ReactDOM from "react-dom/client";
import {
  CometChatUIKit,
  UIKitSettingsBuilder,
} from "@cometchat/chat-uikit-react";
import React from "react";
import App from "App";
import { setupLocalization } from "CometChat/utils/utils";
import { CometChatProvider } from "CometChat/context/CometChatContext";
import { installDebugLog, dlog } from "CometChat/utils/debugLog";

/**
 * Brand logo for boot/error screens. Loaded from the storefront's CDN
 * (same image we use on the login screen) so the user-visible branding
 * is consistent across all states.
 */
const BRAND_LOGO_URL =
  "https://cdn.shopify.com/s/files/1/0677/2647/5492/files/Redan_Chequy_1_sq.webp?v=1770941679";

// Install the on-screen debug log + global error handlers FIRST, before
// any other code runs. This means even errors thrown during the SDK init
// (which is the most common silent-fail point inside a mobile webview
// iframe) get captured. Append `?debug=1` to the iframe URL to make the
// panel visible.
installDebugLog();

export const COMETCHAT_CONSTANTS = {
  APP_ID: process.env.REACT_APP_COMETCHAT_APP_ID || "",
  REGION: process.env.REACT_APP_COMETCHAT_REGION || "us",
  AUTH_KEY: process.env.REACT_APP_COMETCHAT_AUTH_KEY || "",
};

/** Returns true if the uid is missing or an unrendered Liquid tag */
function isInvalidUid(uid: string | null): boolean {
  if (!uid || uid.trim() === "") return true;
  if (uid.includes("{{") || uid.includes("}}")) return true;
  return false;
}

const params = new URLSearchParams(window.location.search);
const uid = params.get("uid");
const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// GATE 1: No valid uid — user is not logged into Shopify
if (isInvalidUid(uid)) {
  root.render(
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      fontFamily: "sans-serif",
      textAlign: "center",
      padding: "24px",
      color: "#555",
    }}>
      <img src={BRAND_LOGO_URL} alt="Redan" style={{ width: "100px", height: "auto", marginBottom: "20px" }} />
      <p style={{ fontSize: "15px" }}>
        Please <strong>log into the store</strong> to access the community chat.
      </p>
    </div>
  );
// GATE 2: Credentials missing
} else if (
  !COMETCHAT_CONSTANTS.APP_ID ||
  !COMETCHAT_CONSTANTS.REGION ||
  !COMETCHAT_CONSTANTS.AUTH_KEY
) {
  root.render(
    <div className="App" style={{ gap: "20px" }}>
      <div className="cometchat-credentials__logo">
        <img src={BRAND_LOGO_URL} alt="Redan" style={{ width: "100px", height: "auto" }} />
      </div>
      <div className="cometchat-credentials__header">
        Chat configuration is incomplete. Please contact support.
      </div>
    </div>
  );
// GATE 3: All good — init CometChat and render the app
} else {
  const uiKitSettings = new UIKitSettingsBuilder()
    .setAppId(COMETCHAT_CONSTANTS.APP_ID)
    .setRegion(COMETCHAT_CONSTANTS.REGION)
    .setAuthKey(COMETCHAT_CONSTANTS.AUTH_KEY)
    .subscribePresenceForAllUsers()
    .build();

  dlog.info("Initializing CometChat UIKit...", {
    appId: COMETCHAT_CONSTANTS.APP_ID,
    region: COMETCHAT_CONSTANTS.REGION,
  });

  // Wrap init in a timeout so a network or storage hang during SDK boot
  // doesn't leave the user staring at a blank screen forever.
  const initPromise = CometChatUIKit.init(uiKitSettings);
  const INIT_TIMEOUT_MS = 25000;
  let initTimedOut = false;
  const timer = setTimeout(() => {
    initTimedOut = true;
    dlog.error(
      `CometChatUIKit.init timed out after ${INIT_TIMEOUT_MS}ms — ` +
        "this usually means storage/network is blocked in the iframe.",
    );
    root.render(
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "sans-serif",
          textAlign: "center",
          padding: "24px",
          color: "#555",
        }}
      >
        <img
          src={BRAND_LOGO_URL}
          alt="Redan"
          style={{ width: "100px", height: "auto", marginBottom: "20px" }}
        />
        <p style={{ fontSize: "15px", maxWidth: "320px" }}>
          The chat couldn't connect. Please check your connection and reload
          the page. If this keeps happening, try opening the chat in your
          browser instead of the app.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "16px",
            padding: "8px 16px",
            backgroundColor: "#260e00",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>,
    );
  }, INIT_TIMEOUT_MS);

  initPromise
    ?.then(() => {
      if (initTimedOut) return;
      clearTimeout(timer);
      dlog.info("CometChatUIKit.init resolved.");
      setupLocalization();
      root.render(
        <CometChatProvider>
          <App />
        </CometChatProvider>,
      );
    })
    .catch((err: unknown) => {
      if (initTimedOut) return;
      clearTimeout(timer);
      dlog.error("CometChatUIKit.init rejected:", err);
      root.render(
        <div className="App" style={{ gap: "20px" }}>
          <div className="cometchat-credentials__logo">
            <img src={BRAND_LOGO_URL} alt="Redan" style={{ width: "100px", height: "auto" }} />
          </div>
          <div className="cometchat-credentials__header">
            Could not initialize chat. Please reload the page.
          </div>
        </div>,
      );
    });
}
